import { isModuleUsable, MODULE_IDS, type ModuleId, type OverrideMode } from '@tahi/core';
import type { MeResponse } from './me';

export type Usable = (moduleId: ModuleId) => boolean;

export interface ModuleStateLike {
  moduleId: string;
  enabled: boolean;
  createdAt: string;
  deletedAt: string | null;
  settings?: unknown;
}

export function overrideFor(me: MeResponse | null, id: ModuleId): OverrideMode {
  return me?.modules.find((m) => m.id === id)?.override ?? 'default';
}

/**
 * Spec §7 module access. Overrides and billing come from /api/me; `enabled` comes from the local
 * module_states rows, so a toggle made offline takes effect at once (the server's ModuleView.usable
 * only catches up after sync).
 */
export function computeUsable(
  me: MeResponse | null,
  states: readonly ModuleStateLike[],
): Record<ModuleId, boolean> {
  const byId = new Map(states.filter((s) => !s.deletedAt).map((s) => [s.moduleId, s]));
  const out = {} as Record<ModuleId, boolean>;
  for (const id of MODULE_IDS) {
    out[id] =
      id === 'today' ||
      isModuleUsable({
        moduleId: id,
        enabled: Boolean(byId.get(id)?.enabled),
        override: overrideFor(me, id),
        billingLive: me?.settings.billingLive ?? false,
        paidOrFree: true, // v0.9 billing replaces this with the user's plan
      });
  }
  return out;
}

/** module_states.settings.navPin (conventions §15): 1..3 pinned, -1 unpinned, undefined = no choice. */
export function navPinOf(state: ModuleStateLike): number | undefined {
  const v = (state.settings as { navPin?: unknown } | null | undefined)?.navPin;
  return typeof v === 'number' ? v : undefined;
}

/**
 * Bottom-nav pins: the user's choice if they made one in Settings, otherwise the first `max`
 * usable, registered modules in the order they were turned on.
 */
export function pinnedModules(
  usable: Usable,
  states: readonly ModuleStateLike[],
  registered: readonly ModuleId[],
  max = 3,
): ModuleId[] {
  const live = states.filter((s) => !s.deletedAt);
  const candidates = registered.filter((id) => id !== 'today' && usable(id));
  const pins = new Map(live.map((s) => [s.moduleId, navPinOf(s)]));
  if (live.some((s) => navPinOf(s) !== undefined)) {
    return candidates
      .filter((id) => (pins.get(id) ?? -1) > 0)
      .sort((a, b) => (pins.get(a) ?? 0) - (pins.get(b) ?? 0))
      .slice(0, max);
  }
  const since = new Map(live.map((s) => [s.moduleId, s.createdAt]));
  const key = (id: ModuleId) => since.get(id) ?? '￿';
  return [...candidates].sort((a, b) => key(a).localeCompare(key(b))).slice(0, max);
}
